<template>
  <section class="reassign-job">
    <div class="reassign-jobs-dropdown-component">
      <div class="reassign-jobs-sites">
        <b-dropdown
          :scrollable="true"
          v-if="isReassignJobsActive"
          :label="$t('select_user_site')"
          v-model="selectedSite"
          aria-role="list"
          position="is-bottom-right"
          append-to-body
        >
          <template #trigger="{ active }">
            <b-tooltip
              :label="selectedSiteLabel"
              position="is-bottom"
              append-to-body
            >
              <b-button
                type="is-primary"
                :icon-right="active ? 'menu-up' : 'menu-down'"
                class="reassign-trigger-button"
              >
                <span class="reassign-trigger-label">{{
                  selectedSiteLabel
                }}</span>
              </b-button>
            </b-tooltip>
          </template>
          <b-dropdown-item
            v-for="(site, index) in sites"
            :key="index"
            :value="site"
            aria-role="listitem"
          >
            <div class="media">
              <div class="media-content">
                <h3>{{ site.siteName }}</h3>
              </div>
            </div>
          </b-dropdown-item>
        </b-dropdown>
      </div>
      <div class="reassign-jobs-users">
        <b-dropdown
          :scrollable="true"
          v-if="isReassignJobsActive"
          v-model="selectedUser"
          :disabled="!selectedSite || !users || users.length === 0"
          aria-role="list"
          position="is-bottom-right"
          append-to-body
        >
          <template #trigger="{ active }">
            <b-tooltip
              :label="selectedUserLabel"
              position="is-bottom"
              append-to-body
            >
              <b-button
                type="is-primary"
                :icon-right="active ? 'menu-up' : 'menu-down'"
                class="reassign-trigger-button"
              >
                <span class="reassign-trigger-label">{{
                  selectedUserLabel
                }}</span>
              </b-button>
            </b-tooltip>
          </template>
          <b-dropdown-item
            v-for="(user, index) in sortedUsers"
            :key="index"
            :value="user"
            aria-role="listitem"
          >
            <div class="media">
              <div class="media-content">
                <h3>{{ user.fullname + ' - ' + user._id }}</h3>
              </div>
            </div>
          </b-dropdown-item>
        </b-dropdown>

        <progress-task-bar
          v-if="isReassignJobsActive && selectedSite && isUsersLoading"
          :demo="true"
          message="Loading users from selected site..."
        />
      </div>

      <div class="reassign-jobs-actions" v-if="$slots['top-actions']">
        <slot name="top-actions"></slot>
      </div>
    </div>

    <div
      v-if="
        isReassignJobsActive &&
        enableGranularSelection &&
        selectedSite &&
        selectedUser
      "
      class="reassign-selection-container"
    >
      <div class="reassign-selection-panel">
        <b-loading
          :is-full-page="false"
          :active.sync="isOwnedItemsLoading"
          :can-cancel="false"
        ></b-loading>
        <div class="reassign-panel-description">
          <p>
            Select specific templates or jobs to reassign. Items can be
            identified by their title, site, and document or project number.
          </p>
        </div>

        <progress-task-bar
          v-if="isOwnedItemsLoading"
          message="Loading templates and jobs for the selected user..."
        />

        <div class="reassign-columns">
          <div class="reassign-column">
            <div class="reassign-column-header">
              <span>Templates ({{ ownedTemplates.length }})</span>
            </div>
            <reassign-items-panel
              title=""
              helperText=""
              :items="filteredOwnedTemplates"
              :selectedIds="selectedTemplateIds"
              emptyStateText="No templates available for reassignment."
              :showFilter="true"
              :filterValue="templatesFilter"
              filterPlaceholder="Filter templates by name or site"
              :showDocumentNumber="true"
              :showSelectAll="true"
              :selectAllValue="allTemplatesSelected"
              @filterInput="templatesFilter = $event"
              @selectAll="toggleAllTemplates"
              @toggle="
                ({ id, selected }) => toggleSelection('template', id, selected)
              "
            />
          </div>

          <div class="reassign-column">
            <div class="reassign-column-header">
              <span>Jobs ({{ ownedJobs.length }})</span>
            </div>
            <reassign-items-panel
              title=""
              helperText=""
              :items="filteredOwnedJobs"
              :selectedIds="selectedJobIds"
              emptyStateText="No jobs available for reassignment."
              :showFilter="true"
              :filterValue="jobsFilter"
              filterPlaceholder="Filter jobs by name or site"
              :showProjectInfoText="true"
              :showSelectAll="true"
              :selectAllValue="allJobsSelected"
              @filterInput="jobsFilter = $event"
              @selectAll="toggleAllJobs"
              @toggle="
                ({ id, selected }) => toggleSelection('job', id, selected)
              "
            />
          </div>
        </div>
      </div>
    </div>

    <p
      v-if="
        isReassignJobsActive &&
        enableGranularSelection &&
        (!selectedSite || !selectedUser)
      "
      class="reassign-step-hint reassign-panel-hint"
    >
      Choose destination site first, then choose destination user to continue.
    </p>
  </section>
</template>

<script>
import client from '../../config/api';
import ProgressTaskBar from '../Shared/ProgressTaskBar';
import ReassignItemsPanel from './ReassignItemsPanel';

export default {
  components: {
    ProgressTaskBar,
    ReassignItemsPanel,
  },
  props: {
    isReassignJobsActive: Boolean,
    currentOwner: Object,
    enableGranularSelection: {
      type: Boolean,
      default: false,
    },
    ownerId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      selectedSite: null,
      selectedUser: null,
      users: null,
      usersBySiteCache: {},
      isUsersLoading: false,
      isOwnedItemsLoading: false,
      ownedJobs: [],
      ownedTemplates: [],
      selectedJobIds: [],
      selectedTemplateIds: [],
      jobsFilter: '',
      templatesFilter: '',
    };
  },
  computed: {
    selectedSiteLabel() {
      return this.selectedSite
        ? this.selectedSite.siteName
        : 'Select the user site';
    },
    selectedUserLabel() {
      if (!this.selectedSite) {
        return 'Select a site first to load users.';
      }

      return this.selectedUser
        ? `${this.selectedUser.fullname} - ${this.selectedUser._id}`
        : 'Select a user to reassign the jobs';
    },
    sites() {
      const { sites } = this.$store.state;
      return sites;
    },

    sortedUsers() {
      if (this.users && this.users.length > 0) {
        const sorted = [...this.users].sort((a, b) =>
          a.fullname > b.fullname ? 1 : -1
        );

        if (this.currentOwner && this.currentOwner._id) {
          return sorted.filter((user) => user._id !== this.currentOwner._id);
        }

        return sorted;
      }

      return [];
    },
    filteredOwnedJobs() {
      const query = (this.jobsFilter || '').trim().toLowerCase();
      if (!query) {
        return this.ownedJobs;
      }

      return this.ownedJobs.filter((job) => {
        const title = (job.title || '').toLowerCase();
        const site = (job.siteName || '').toLowerCase();
        const projectInfoText = (job.projectInfoText || '').toLowerCase();
        return (
          title.includes(query) ||
          site.includes(query) ||
          (projectInfoText && projectInfoText.includes(query))
        );
      });
    },
    filteredOwnedTemplates() {
      const query = (this.templatesFilter || '').trim().toLowerCase();
      if (!query) {
        return this.ownedTemplates;
      }

      return this.ownedTemplates.filter((templateItem) => {
        const title = (templateItem.title || '').toLowerCase();
        const site = (templateItem.siteName || '').toLowerCase();
        const documentNumber = (templateItem.documentNumber || '')
          .toLowerCase()
          .trim();
        return (
          title.includes(query) ||
          site.includes(query) ||
          (documentNumber && documentNumber.includes(query))
        );
      });
    },
    allTemplatesSelected() {
      return (
        this.ownedTemplates.length > 0 &&
        this.ownedTemplates.every((item) =>
          this.selectedTemplateIds.includes(item._id)
        )
      );
    },
    allJobsSelected() {
      return (
        this.ownedJobs.length > 0 &&
        this.ownedJobs.every((item) => this.selectedJobIds.includes(item._id))
      );
    },
  },
  watch: {
    isReassignJobsActive: {
      immediate: true,
      handler(active) {
        if (active && this.enableGranularSelection) {
          this.fetchOwnedItems();
        }
      },
    },

    ownerId(newOwnerId, oldOwnerId) {
      if (
        this.enableGranularSelection &&
        this.isReassignJobsActive &&
        newOwnerId &&
        newOwnerId !== oldOwnerId
      ) {
        this.fetchOwnedItems();
      }
    },

    selectedSite(newSite, oldSite) {
      if (!newSite) {
        this.users = [];
        this.selectedUser = null;
        return;
      }

      if ((!oldSite && newSite) || newSite._id !== oldSite._id) {
        this.users = [];
        this.selectedUser = null;
        this.getUsersBySite(newSite._id);
        this.$emit('siteSelected', newSite);
      }
    },
    selectedUser(newUser, oldUser) {
      if (!newUser) {
        return;
      }

      if ((!oldUser && newUser) || newUser._id !== oldUser._id) {
        this.$emit('userSelected', newUser);
      }
    },
  },

  async mounted() {
    const parsedSites = JSON.parse(JSON.stringify(this.$store.state.sites));
    if (parsedSites.length < 1) {
      await this.$store.dispatch('getSites');
    }

    if (!this.sites || this.sites.length < 1) {
      await this.$store.dispatch('getSites');
    }
  },

  methods: {
    refresh() {
      this.selectedSite = null;
      this.selectedUser = null;
      this.users = null;
      this.usersBySiteCache = {};
      this.ownedJobs = [];
      this.ownedTemplates = [];
      this.selectedJobIds = [];
      this.selectedTemplateIds = [];
      this.jobsFilter = '';
      this.templatesFilter = '';
      this.$emit('selectionChanged', { selectedJobIds: [] });
    },
    async reassignUserJobs() {
      if (!this.isReassignJobsActive) {
        this.isReassignJobsActive = true;
      } else {
        this.isReassignJobsActive = false;
      }
    },

    async getUsersBySite(siteId) {
      if (this.usersBySiteCache[siteId]) {
        this.users = this.usersBySiteCache[siteId];
        return;
      }

      this.isUsersLoading = true;
      try {
        const { data: users } = await client.get('/users', {
          params: {
            location: siteId,
          },
        });
        this.usersBySiteCache = {
          ...this.usersBySiteCache,
          [siteId]: users,
        };
        this.users = users;
      } finally {
        this.isUsersLoading = false;
      }
    },

    async fetchOwnedItems() {
      const selectedOwnerId = this.ownerId || this.currentOwner?._id;
      if (!selectedOwnerId) {
        this.ownedJobs = [];
        this.ownedTemplates = [];
        return;
      }

      this.isOwnedItemsLoading = true;
      try {
        const result = await this.$store.dispatch(
          'getUserOwnedReassignItems',
          selectedOwnerId
        );

        this.ownedTemplates = result?.templates || [];
        this.ownedJobs = result?.jobs || [];

        this.selectedTemplateIds = this.selectedTemplateIds.filter((id) =>
          this.ownedTemplates.some((item) => item._id === id)
        );
        this.selectedJobIds = this.selectedJobIds.filter((id) =>
          this.ownedJobs.some((item) => item._id === id)
        );

        this.emitSelectionChanged();
      } finally {
        this.isOwnedItemsLoading = false;
      }
    },

    toggleSelection(type, id, isSelected) {
      const targetArray =
        type === 'template' ? this.selectedTemplateIds : this.selectedJobIds;

      if (isSelected) {
        if (!targetArray.includes(id)) {
          targetArray.push(id);
        }
      } else {
        const index = targetArray.indexOf(id);
        if (index >= 0) {
          targetArray.splice(index, 1);
        }
      }

      this.emitSelectionChanged();
    },

    toggleAllTemplates(isSelected) {
      this.selectedTemplateIds = isSelected
        ? this.ownedTemplates.map((item) => item._id)
        : [];
      this.emitSelectionChanged();
    },

    toggleAllJobs(isSelected) {
      this.selectedJobIds = isSelected
        ? this.ownedJobs.map((item) => item._id)
        : [];
      this.emitSelectionChanged();
    },

    emitSelectionChanged() {
      this.$emit('selectionChanged', {
        selectedJobIds: [...this.selectedTemplateIds, ...this.selectedJobIds],
        selectedTemplateIds: [...this.selectedTemplateIds],
        selectedWorkOrderIds: [...this.selectedJobIds],
      });
    },
  },
};
</script>

<style lang="scss">
.reassign-jobs-dropdown-component {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
  align-items: center;
  flex-shrink: 0;

  .reassign-jobs-users {
    margin-left: 0;
  }

  .reassign-jobs-sites,
  .reassign-jobs-users {
    display: inline-flex;
    align-items: center;
  }

  .reassign-jobs-actions {
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .dropdown-content {
    margin-bottom: 20px;
  }
}

.reassign-trigger-button {
  width: 280px;
  height: 36px;
  display: inline-flex;
  align-items: center;
}

.reassign-trigger-label {
  display: inline-block;
  width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
  text-align: left;
}

.reassign-job {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.reassign-selection-container {
  margin-top: 12px;
  flex: 1;
  min-height: 0;
}

.reassign-step-hint {
  margin-top: 10px;
}

.reassign-panel-hint {
  margin-top: 12px;
}

.reassign-selection-panel {
  border: 1px solid;
  border-radius: 4px;
  padding: 8px;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;

  .reassign-panel-description {
    margin: 4px 4px 10px;
  }

  .reassign-columns {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    flex: 1;
    min-height: 0;
    height: 100%;
    overflow: hidden;
  }

  .reassign-column {
    border-top: 1px solid;
    padding-top: 8px;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .reassign-column-header {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
  }

  .reassign-selection-column {
    padding: 4px;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
  }

  .reassign-selection-item {
    margin-bottom: 6px;
  }

  .helper,
  .empty-state {
    font-size: 12px;
    margin-bottom: 8px;
  }
}

@media (max-width: 1024px) {
  .reassign-selection-panel {
    .reassign-columns {
      grid-template-columns: 1fr;
    }
  }
}
</style>
